import {
  isValidElement,
  useMemo,
  useState,
} from "react";

import "./DataTable.css";

const getPlainText = (value) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getCellValue = (column, row) => {
  const originalValue = row[column.data];

  if (typeof column.render === "function") {
    return column.render(
      originalValue,
      "display",
      row
    );
  }

  return originalValue ?? "";
};

const getExportValue = (column, row) => {
  const renderedValue = getCellValue(column, row);

  if (isValidElement(renderedValue)) {
    return getPlainText(
      renderedValue.props?.children
    );
  }

  return getPlainText(renderedValue);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

function DataTable({
  data = [],
  columns = [],
  onEdit,
  onToggleActive,
  exportFileName = "reporte",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const hasActions =
    typeof onEdit === "function" ||
    typeof onToggleActive === "function";

  const filteredData = useMemo(() => {
    const normalizedSearch = searchTerm
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return data;
    }

    return data.filter((row) =>
      columns.some((column) =>
        getExportValue(column, row)
          .toLowerCase()
          .includes(normalizedSearch)
      )
    );
  }, [columns, data, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) {
      return filteredData;
    }

    const selectedColumn = columns.find(
      (column) => column.data === sortConfig.key
    );

    if (!selectedColumn) {
      return filteredData;
    }

    return [...filteredData].sort((firstRow, secondRow) => {
      const firstValue = getExportValue(
        selectedColumn,
        firstRow
      );

      const secondValue = getExportValue(
        selectedColumn,
        secondRow
      );

      const firstNumber = Number(
        String(firstValue)
          .replace(/[^\d,.-]/g, "")
          .replace(/\./g, "")
          .replace(",", ".")
      );

      const secondNumber = Number(
        String(secondValue)
          .replace(/[^\d,.-]/g, "")
          .replace(/\./g, "")
          .replace(",", ".")
      );

      let comparison = 0;

      if (
        !Number.isNaN(firstNumber) &&
        !Number.isNaN(secondNumber) &&
        firstValue !== "" &&
        secondValue !== ""
      ) {
        comparison = firstNumber - secondNumber;
      } else {
        comparison = String(firstValue).localeCompare(
          String(secondValue),
          "es",
          {
            numeric: true,
            sensitivity: "base",
          }
        );
      }

      return sortConfig.direction === "asc"
        ? comparison
        : -comparison;
    });
  }, [columns, filteredData, sortConfig]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedData.length / pageSize)
  );

  const safeCurrentPage = Math.min(
    currentPage,
    totalPages
  );

  const pageStart = (safeCurrentPage - 1) * pageSize;

  const visibleRows = sortedData.slice(
    pageStart,
    pageStart + pageSize
  );

  const firstRecord =
    sortedData.length === 0 ? 0 : pageStart + 1;

  const lastRecord = Math.min(
    pageStart + pageSize,
    sortedData.length
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (!column.data) {
      return;
    }

    setSortConfig((previous) => ({
      key: column.data,
      direction:
        previous.key === column.data &&
        previous.direction === "asc"
          ? "desc"
          : "asc",
    }));

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column.data) {
      return "↕";
    }

    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const downloadExcel = () => {
    const headerHtml = columns
      .map(
        (column) =>
          `<th>${escapeHtml(column.title)}</th>`
      )
      .join("");

    const rowsHtml = sortedData
      .map((row) => {
        const cells = columns
          .map(
            (column) =>
              `<td>${escapeHtml(
                getExportValue(column, row)
              )}</td>`
          )
          .join("");

        return `<tr>${cells}</tr>`;
      })
      .join("");

    const excelHtml = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            table {
              border-collapse: collapse;
              font-family: Arial, sans-serif;
              font-size: 11pt;
            }

            th {
              padding: 10px;
              color: #ffffff;
              background: #e600a9;
              text-align: left;
            }

            td {
              padding: 8px;
              border: 1px solid #dce6f0;
            }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>${headerHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob(
      ["\ufeff", excelHtml],
      {
        type: "application/vnd.ms-excel;charset=utf-8;",
      }
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${exportFileName}.xls`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const headerHtml = columns
      .map(
        (column) =>
          `<th>${escapeHtml(column.title)}</th>`
      )
      .join("");

    const rowsHtml = sortedData
      .map((row) => {
        const cells = columns
          .map(
            (column) =>
              `<td>${escapeHtml(
                getExportValue(column, row)
              )}</td>`
          )
          .join("");

        return `<tr>${cells}</tr>`;
      })
      .join("");

    const printWindow = window.open(
      "",
      "_blank",
      "width=1100,height=760"
    );

    if (!printWindow) {
      window.alert(
        "El navegador bloqueó la ventana de impresión."
      );
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8" />
          <title>${escapeHtml(exportFileName)}</title>
          <style>
            @page {
              size: landscape;
              margin: 14mm;
            }

            body {
              color: #172f55;
              font-family: Arial, sans-serif;
            }

            h1 {
              margin: 0 0 18px;
              font-size: 22px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
            }

            th {
              padding: 10px;
              color: #ffffff;
              background: #e600a9;
              text-align: left;
            }

            td {
              padding: 8px;
              border-bottom: 1px solid #dce6f0;
            }

            tr:nth-child(even) {
              background: #f9fbfe;
            }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(exportFileName)}</h1>

          <table>
            <thead>
              <tr>${headerHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div className="data-table-container">
      <div className="data-table-toolbar">
        <div className="data-table-toolbar-left">
          <label className="data-table-page-size">
            Mostrar
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            registros
          </label>

          <button
            type="button"
            className="data-table-export excel"
            onClick={downloadExcel}
            disabled={sortedData.length === 0}
          >
            ▣ Exportar Excel
          </button>

          <button
            type="button"
            className="data-table-export pdf"
            onClick={exportPdf}
            disabled={sortedData.length === 0}
          >
            ▤ Exportar PDF
          </button>
        </div>

        <label className="data-table-search">
          Buscar:
          <input
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Buscar en la tabla"
          />
        </label>
      </div>

      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.data || column.title}>
                  <button
                    type="button"
                    className="data-table-sort-button"
                    onClick={() => handleSort(column)}
                  >
                    {column.title}
                    <span>{getSortIcon(column)}</span>
                  </button>
                </th>
              ))}

              {hasActions && <th>Acciones</th>}
            </tr>
          </thead>

          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length + (hasActions ? 1 : 0)
                  }
                  className="data-table-empty"
                >
                  No se encontraron registros.
                </td>
              </tr>
            ) : (
              visibleRows.map((row, index) => (
                <tr
                  key={
                    row.id ??
                    `${pageStart}-${index}`
                  }
                >
                  {columns.map((column) => {
                    const value = getCellValue(
                      column,
                      row
                    );

                    return (
                      <td
                        key={
                          column.data || column.title
                        }
                      >
                        {isValidElement(value) ? (
                          value
                        ) : (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: String(value ?? ""),
                            }}
                          />
                        )}
                      </td>
                    );
                  })}

                  {hasActions && (
                    <td className="data-table-actions">
                      {onEdit && (
                        <button
                          type="button"
                          className="data-table-edit"
                          onClick={() => onEdit(row)}
                        >
                          Editar
                        </button>
                      )}

                      {onToggleActive && (
                        <button
                          type="button"
                          className={
                            row.is_active
                              ? "data-table-disable"
                              : "data-table-enable"
                          }
                          onClick={() =>
                            onToggleActive(row)
                          }
                        >
                          {row.is_active
                            ? "Inactivar"
                            : "Activar"}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="data-table-footer">
        <span>
          Mostrando {firstRecord} a {lastRecord} de{" "}
          {sortedData.length} registros
        </span>

        <div className="data-table-pagination">
          <button
            type="button"
            onClick={() => setCurrentPage(1)}
            disabled={safeCurrentPage === 1}
          >
            Primero
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentPage((page) =>
                Math.max(1, page - 1)
              )
            }
            disabled={safeCurrentPage === 1}
          >
            Anterior
          </button>

          <span className="data-table-current-page">
            {safeCurrentPage}
          </span>

          <button
            type="button"
            onClick={() =>
              setCurrentPage((page) =>
                Math.min(totalPages, page + 1)
              )
            }
            disabled={safeCurrentPage === totalPages}
          >
            Siguiente
          </button>

          <button
            type="button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={safeCurrentPage === totalPages}
          >
            Último
          </button>
        </div>
      </div>
    </div>
  );
}

export default DataTable;